import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ReorderIcon from '@mui/icons-material/Reorder'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { type NormalizedDimension } from './schemas'

type ProductDimensionDraggableProps = {
    dimension: NormalizedDimension
    index: number
    isNewProduct: boolean
    isSingleDimension: boolean
    canReorder: boolean
    onEdit: (dimension: NormalizedDimension) => void
    onRemove: (index: number) => void
}

export default function ProductDimensionDraggable({
    dimension,
    index,
    isNewProduct,
    isSingleDimension,
    canReorder,
    onEdit,
    onRemove,
}: ProductDimensionDraggableProps) {
    const { setNodeRef, setActivatorNodeRef, attributes, listeners, transition, transform } =
        useSortable({ id: dimension.id })

    const shouldShowReorderButton = isNewProduct && !isSingleDimension

    return (
        <Stack
            gap={2}
            ref={setNodeRef}
            style={{
                transition,
                transform: CSS.Transform.toString(transform),
            }}
            {...attributes}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <div>
                    <Typography gutterBottom variant="body1" fontWeight={500}>
                        {dimension.name}
                    </Typography>

                    <Stack direction="row" gap={1} flexWrap="wrap">
                        {dimension.options.map(({ index: optionIndex, name: optionName }) => (
                            <Chip
                                variant="outlined"
                                key={optionIndex}
                                label={optionName}
                                sx={{ borderRadius: 1 }}
                            />
                        ))}
                    </Stack>
                </div>

                <div>
                    {isNewProduct && (
                        <Tooltip title="Remove dimension">
                            <IconButton
                                aria-label="remove dimension"
                                onClick={() => onRemove(index)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip title="Edit dimension">
                        <IconButton aria-label="edit dimension" onClick={() => onEdit(dimension)}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>

                    {shouldShowReorderButton && (
                        <Tooltip title="Reorder dimensions">
                            <IconButton
                                ref={setActivatorNodeRef}
                                aria-label="reorder dimensions"
                                disabled={canReorder}
                                {...listeners}
                            >
                                <ReorderIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            </Stack>
            <Divider />
        </Stack>
    )
}
